import { MagnifyingGlass, SmileyMelting, X } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { Input } from "@shopflo/ui/input";
import { hexToRgba } from "@shopflo/ui/utils";
import defaultPackageImage from "assests/images/package.png";
import Price from "components/common/Price";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useLocale } from "lib/hooks/useLocale";
import { DiscountAchieverVariant, getInventoryCap } from "lib/types/discountAchiever";
import { classNames, truncateString } from "lib/utils/helpers";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useMemo, useState } from "react";

interface ItemVariantSelectorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  imageUrl: string;
  variants: DiscountAchieverVariant[];
  /** variant_id of the variant currently in the cart */
  selectedVariantId: string;
  isLoading: boolean;
  onConfirm: (variantId: string, availableQuantity: number) => Promise<void>;
}

const ItemVariantSelectorDialog: React.FC<ItemVariantSelectorDialogProps> = ({
  isOpen,
  onClose,
  productName,
  imageUrl,
  variants,
  selectedVariantId,
  isLoading,
  onConfirm,
}) => {
  const { t } = useLocale();
  const {
    state: { merchant },
  } = useMerchantContext();
  const primaryColor = merchant?.colorPallet?.primaryColor ?? "#fff";
  const [draftVariantId, setDraftVariantId] = useState<string>(selectedVariantId);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Reopening the sheet after a swap (or an abandoned pick) must start from what is in the cart.
  useEffect(() => {
    if (isOpen) {
      setDraftVariantId(selectedVariantId);
      setIsSearchOpen(false);
      setSearchQuery("");
    }
  }, [isOpen, selectedVariantId]);

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery("");
  };

  const filteredVariants = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matches = query
      ? variants.filter((variant) => variant.name?.toLowerCase().includes(query))
      : variants;
    return [...matches].sort((a, b) =>
      a.variant_id === draftVariantId ? -1 : b.variant_id === draftVariantId ? 1 : 0,
    );
  }, [variants, searchQuery, draftVariantId]);

  if (!variants?.length) return null;

  return (
    <GenericDialog
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      translateAxis="y"
      customClass="!rounded-t-2xl"
      closeOnOverlayClick
      showExternalClose>
      <div className="flex flex-col min-h-[35dvh] max-h-[70dvh] ">
        <div className="flex w-full shrink-0 flex-row items-center justify-between overflow-hidden px-5 py-4">
          <AnimatePresence mode="wait" initial={false}>
            {isSearchOpen ? (
              <motion.div
                key="search"
                initial={{ y: 16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 16, opacity: 0 }}
                transition={{ duration: 0.1, ease: "easeOut" }}
                className="flex w-full flex-row items-center py-[3px]">
                <Input
                  autoFocus
                  dimension="md"
                  variant="filled"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("search_variants_field")}
                  className="w-full"
                  startAdornment={
                    <Icon
                      IconComponent={MagnifyingGlass}
                      size="xs"
                      iconProps={{ className: "text-gray-dark" }}
                    />
                  }
                  endAdornment={
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={closeSearch}
                      className="flex items-center justify-center">
                      <Icon
                        IconComponent={X}
                        size="xs"
                        iconProps={{ className: "cursor-pointer text-gray-dark" }}
                      />
                    </button>
                  }
                />
              </motion.div>
            ) : (
              <motion.div
                key="header"
                initial={{ y: -16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -16, opacity: 0 }}
                transition={{ duration: 0.1, ease: "easeOut" }}
                className="flex w-full flex-row items-center justify-between">
                <div className="flex flex-col gap-0.5">
                  <p className="text-text-xs-medium text-ink-strong opacity-60">
                    {truncateString(productName, 40)}
                  </p>
                  <p className="text-text-md-semibold text-ink-strong">{t("choose_option")}</p>
                </div>
                <button type="button" aria-label="Search" onClick={() => setIsSearchOpen(true)}>
                  <Icon
                    IconComponent={MagnifyingGlass}
                    size="md"
                    iconProps={{ className: "cursor-pointer text-ink-strong opacity-50" }}
                  />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div
          className={classNames("flex-1 overflow-y-auto scrollbar-hide  pb-8")}
          style={{
            alignContent: filteredVariants.length == 0 ? "center" : "",
          }}>
          {filteredVariants.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <SmileyMelting size={48} weight="fill" className="cursor-pointer text-ink-strong opacity-30" />
              <p className=" text-center text-text-sm-medium text-ink-strong opacity-70">
                {t("no_variants_found")}
              </p>
            </div>
          ) : (
            <ul className="flex flex-col space-y-2 px-4" role="list">
              {filteredVariants.map((variant, index) => {
                const price = variant.discounted_price ?? variant.current_price;
                const compareAtPrice = variant.original_price > price ? variant.original_price : undefined;
                const isSelected = draftVariantId === variant.variant_id;
                // An out-of-stock variant is still shown (merchants expect to see it) but cannot be
                // switched to — except when it is the one already in the cart.
                const isDisabled = variant.out_of_stock && variant.variant_id !== selectedVariantId;

                  return (
                    <li
                      key={variant.variant_id || `variant-${index}`}
                      role="radio"
                      aria-checked={isSelected}
                      aria-disabled={isDisabled}
                      onClick={async () => {
                        if (isDisabled || isLoading || isSelected) return;
                        await onConfirm(variant.variant_id, getInventoryCap(variant));
                        setDraftVariantId(variant.variant_id);
                      }}
                      className={classNames(
                        "flex w-full snap-start flex-row items-center gap-3 rounded-xl  p-2",
                        isSelected ? "border border-primary-dark " : " hover:bg-black/5",
                        isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                      )}
                      style={{
                        backgroundColor: isSelected ? hexToRgba(primaryColor, 0.08) : "",
                        borderWidth:isSelected?'1.5px':'0px'
                      }}>
                      <div className="relative h-[3.5rem] min-h-[3.5rem] w-[3.5rem] min-w-[3.5rem] shrink-0 overflow-hidden rounded-lg">
                        {/* Decorative inset border — sits above the image but must never swallow clicks meant for the row */}
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-0 z-10 rounded-lg"
                          style={{ boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.08)" }}
                        />
                        <img
                          src={variant.imageUrl ?? imageUrl}
                          alt={`${productName} - ${variant.name}`}
                          className="h-full w-full object-cover"
                          loading="lazy"
                          onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = defaultPackageImage;
                          }}
                        />
                      </div>
                      <div className="flex w-full flex-col space-y-1">
                        <span className="text-text-sm-medium text-ink-strong opacity-80">
                          {truncateString(variant.name, 40)}
                        </span>
                        <Price total={price} compareAt={compareAtPrice} orientation="horizontal" size="md" />
                        {variant.out_of_stock && (
                          <span className="text-text-xs-medium text-ouch">{t("oos_item")}</span>
                        )}
                      </div>
                      {/* <div
                        className={classNames(
                          "flex h-5 w-5 min-w-[1.25rem] items-center justify-center rounded-full border",
                          isSelected ? "border-primary-dark bg-primary-dark" : "border-gray-dark",
                        )}>
                        {isSelected && (
                          <Icon
                            IconComponent={Check}
                            size="xxs"
                            iconProps={{ weight: "bold", className: "text-btnPrimaryText" }}
                          />
                        )}
                      </div> */}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </GenericDialog>
  );
};

export default ItemVariantSelectorDialog;
